/**
 * Statistics React Query hooks
 * Handles fetching system and dashboard statistics
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import * as systemApi from '@/services/systemApi';
import { STALE_TIME } from '@/utils/constants';
import { useStatsUpdateSocket, useDashboardSubscription } from './useSocket';
import type { StatsUpdateEvent } from '@/types/socket';
import type { JobStats, ActivityResponse } from '@/types/job';
import type { HealthResponse, SystemStats } from '@/types/api';

/**
 * Query keys for stats
 */
export const statsKeys = {
  all: ['stats'] as const,
  health: () => [...statsKeys.all, 'health'] as const,
  system: () => [...statsKeys.all, 'system'] as const,
  jobs: () => [...statsKeys.all, 'jobs'] as const,
  activity: (period: string) => [...statsKeys.all, 'activity', period] as const,
  dashboard: () => [...statsKeys.all, 'dashboard'] as const,
};

/**
 * Hook to fetch system health
 */
export function useHealth() {
  return useQuery({
    queryKey: statsKeys.health(),
    queryFn: systemApi.getHealth,
    staleTime: STALE_TIME.HEALTH,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Hook to fetch system stats
 */
export function useSystemStats() {
  return useQuery({
    queryKey: statsKeys.system(),
    queryFn: systemApi.getSystemStats,
    staleTime: STALE_TIME.STATS,
  });
}

/**
 * Hook to fetch job stats
 */
export function useJobStats() {
  return useQuery({
    queryKey: statsKeys.jobs(),
    queryFn: systemApi.getJobStats,
    staleTime: STALE_TIME.STATS,
  });
}

/**
 * Hook to fetch activity data for charts
 */
export function useActivityData(period: 'day' | 'week' | 'month' = 'week') {
  return useQuery({
    queryKey: statsKeys.activity(period),
    queryFn: () => systemApi.getActivityData(period),
    staleTime: STALE_TIME.STATS,
  });
}

/**
 * Hook to fetch all dashboard data at once
 */
export function useDashboardData() {
  return useQuery({
    queryKey: statsKeys.dashboard(),
    queryFn: systemApi.getDashboardStats,
    staleTime: STALE_TIME.STATS,
  });
}

/**
 * Hook for dashboard stats with real-time updates
 */
export function useDashboardStats() {
  const queryClient = useQueryClient();

  // Fetch initial data
  const { data: health, isLoading: healthLoading } = useHealth();
  const { data: jobStats, isLoading: jobStatsLoading } = useJobStats();
  const { data: activity, isLoading: activityLoading } = useActivityData('week');

  // Subscribe to dashboard updates
  useDashboardSubscription();

  // Handle real-time stats updates
  const handleStatsUpdate = useCallback(
    (data: StatsUpdateEvent) => {
      // Update job stats cache
      queryClient.setQueryData(statsKeys.jobs(), (old: JobStats | undefined) => {
        if (!old) return old;
        return {
          ...old,
          totalJobs: data.totalJobs,
          runningJobs: data.runningJobs,
          pendingJobs: data.pendingJobs,
          completedJobs: data.completedJobs,
          failedJobs: data.failedJobs,
          totalPlacesScraped: data.totalPlaces,
        };
      });

      // Could also update health/system stats if included in the event
    },
    [queryClient]
  );

  // Set up WebSocket listener
  useStatsUpdateSocket(handleStatsUpdate);

  // Build stats object for StatsCards
  const stats = {
    totalJobs: jobStats?.totalJobs ?? 0,
    runningJobs: jobStats?.runningJobs ?? 0,
    pendingJobs: jobStats?.pendingJobs ?? 0,
    completedJobs: jobStats?.completedJobs ?? 0,
    failedJobs: jobStats?.failedJobs ?? 0,
    totalPlaces: jobStats?.totalPlacesScraped ?? 0,
    successRate: jobStats?.successRate ?? 0,
  };

  // Build systemStats object for SystemHealthCard
  const systemStats = {
    database: {
      connected: health?.checks?.database?.status === 'up',
      status: health?.checks?.database?.status ?? 'unknown',
      latency: health?.checks?.database?.latency,
      message: health?.checks?.database?.message,
    },
    cache: {
      connected: health?.checks?.valkey?.status === 'up',
      status: health?.checks?.valkey?.status ?? 'unknown',
      latency: health?.checks?.valkey?.latency,
      message: health?.checks?.valkey?.message,
    },
    workers: {
      active: 0,
      total: 0,
    },
    queue: {
      waiting: 0,
      active: 0,
      failed: 0,
    },
    proxies: {
      healthy: health?.proxies?.healthy ?? 0,
      total: health?.proxies?.total ?? 0,
    },
  };

  // Refetch function
  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: statsKeys.all });
  }, [queryClient]);

  return {
    // For DashboardPage compatibility
    stats,
    activityData: activity?.data ?? [],
    systemStats,
    isLoading: healthLoading || jobStatsLoading || activityLoading,
    refetch,

    // Original values for other components
    health,
    jobStats,
    activity,

    // Derived stats
    totalJobs: stats.totalJobs,
    runningJobs: stats.runningJobs,
    pendingJobs: stats.pendingJobs,
    completedJobs: stats.completedJobs,
    failedJobs: stats.failedJobs,
    totalPlaces: stats.totalPlaces,
    successRate: stats.successRate,

    // Health status
    isHealthy: health?.status === 'healthy',
    isDegraded: health?.status === 'degraded',
    isUnhealthy: health?.status === 'unhealthy',
  };
}

/**
 * Hook to invalidate all stats
 */
export function useInvalidateStats() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: statsKeys.all });
  }, [queryClient]);
}

/**
 * Hook for queue stats
 */
export function useQueueStats() {
  return useQuery({
    queryKey: [...statsKeys.all, 'queue'],
    queryFn: systemApi.getQueueStats,
    staleTime: STALE_TIME.STATS,
    refetchInterval: 10000, // Refresh every 10 seconds
  });
}

/**
 * Hook for database stats
 */
export function useDatabaseStats() {
  return useQuery({
    queryKey: [...statsKeys.all, 'database'],
    queryFn: systemApi.getDatabaseStats,
    staleTime: 60000, // Cache for 1 minute
  });
}

/**
 * Computed stat helpers
 */
export function useStatTrend(
  current: number,
  previous: number
): { trend: 'up' | 'down' | 'neutral'; percentage: number } {
  if (previous === 0) {
    return { trend: 'neutral', percentage: 0 };
  }

  const percentage = ((current - previous) / previous) * 100;

  if (percentage > 0) {
    return { trend: 'up', percentage };
  } else if (percentage < 0) {
    return { trend: 'down', percentage: Math.abs(percentage) };
  }

  return { trend: 'neutral', percentage: 0 };
}

export default useDashboardStats;
